import { useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Badge } from '@/components/Badge';
import { Button } from '@/components/Button';
import { Card, CardBody, EmptyState } from '@/components/Card';
import { TextInput } from '@/components/Field';
import { Modal } from '@/components/Modal';
import { useToast } from '@/components/Toast';
import { assess } from '@/domain/engine';
import { createSampleProject } from '@/domain/sample';
import { buildExportPayload, exportFileName, parseImportPayload } from '@/domain/storage';
import { downloadJson, pickTextFile } from '@/lib/download';
import { formatCurrency, formatMass, formatPercent, formatRatio, formatRelativeTime } from '@/lib/format';
import type { Project } from '@/domain/types';
import { useProjectStore } from '@/app/projectStore';

/** 项目列表：多项目管理的入口 */
export function ProjectsPage() {
  const { projects, createProject, addProject, removeProject, duplicate, storageAvailable, ready } =
    useProjectStore();
  const navigate = useNavigate();
  const toast = useToast();

  const [creating, setCreating] = useState(false);
  const [newName, setNewName] = useState('');
  const [pendingDelete, setPendingDelete] = useState<Project | null>(null);

  const summaries = useMemo(
    () =>
      projects.map((project) => ({ project, assessment: assess(project) })),
    [projects],
  );

  const handleCreate = () => {
    const name = newName.trim() || '未命名项目';
    const project = createProject(name);
    setCreating(false);
    setNewName('');
    toast.ok('已创建项目', '接下来按步骤填写参数，随时可以留空。');
    navigate(`/p/${project.id}/wizard`);
  };

  const handleImport = async () => {
    const file = await pickTextFile();
    if (!file) return;
    const result = parseImportPayload(file.text);
    if (!result.ok) {
      toast.critical('导入失败', result.error);
      return;
    }
    const project = addProject(result.project);
    toast.ok('已导入项目', project.name);
    navigate(`/p/${project.id}`);
  };

  const handleSample = () => {
    const sample = addProject(createSampleProject());
    toast.ok('已载入示例项目', '可以直接改参数，也可以另存为自己的方案。');
    navigate(`/p/${sample.id}`);
  };

  const handleExport = (project: Project) => {
    downloadJson(exportFileName(project), buildExportPayload(project));
    toast.ok('已导出项目 JSON');
  };

  return (
    <div className="flex flex-col gap-4">
      <div className="flex flex-col gap-3 md:flex-row md:items-end md:justify-between">
        <div>
          <h1 className="text-lg font-semibold text-fg md:text-xl">无人机搭建评估</h1>
          <p className="mt-1 text-xs leading-relaxed text-fg-muted">
            按 4 轴填参数，实时给出预估推力、起飞质量、推重比与金额；参数可以留空，缺哪项会直接标出来。
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          <Button size="lg" variant="primary" onClick={() => setCreating(true)}>
            + 新建项目
          </Button>
          <Button size="lg" variant="default" onClick={handleImport}>
            导入 JSON
          </Button>
          <Button size="lg" variant="ghost" onClick={handleSample}>
            载入示例
          </Button>
        </div>
      </div>

      {!storageAvailable ? (
        <Card tone="warning">
          <CardBody>
            <p className="text-xs leading-relaxed text-fg">
              当前浏览器不允许本地存储（可能是隐私模式）。改动只在本标签页内有效，关闭页面后会丢失；建议导出 JSON 保存。
            </p>
          </CardBody>
        </Card>
      ) : null}

      {!ready ? null : projects.length === 0 ? (
        <Card>
          <EmptyState
            title="还没有项目"
            description="新建一个空白项目，按引导一步步填；也可以先载入示例看看效果。"
            action={
              <div className="flex flex-wrap justify-center gap-2">
                <Button size="lg" variant="primary" onClick={() => setCreating(true)}>
                  + 新建项目
                </Button>
                <Button size="lg" variant="default" onClick={handleSample}>
                  载入示例
                </Button>
              </div>
            }
          />
        </Card>
      ) : (
        <div className="grid grid-cols-1 gap-3 md:grid-cols-2 xl:grid-cols-3">
          {summaries.map(({ project, assessment }) => {
            const mass = formatMass(assessment.mass.value?.totalG ?? null);
            return (
              <Card key={project.id} className="flex flex-col">
                <CardBody className="flex flex-1 flex-col gap-3">
                  <div className="flex items-start justify-between gap-2">
                    <Link to={`/p/${project.id}`} className="min-w-0 flex-1">
                      <h2 className="truncate text-sm font-semibold text-fg hover:text-accent">
                        {project.name}
                      </h2>
                      <p className="mt-0.5 text-[11px] text-fg-subtle">
                        更新于 {formatRelativeTime(project.updatedAt)}
                      </p>
                    </Link>
                    <Badge tone={assessment.verdict.tone} solid={assessment.verdict.level !== 'unknown'}>
                      {assessment.verdict.label}
                    </Badge>
                  </div>

                  <div className="tabular grid grid-cols-3 gap-2 border-y border-line py-2 text-center">
                    <div className="flex flex-col gap-0.5">
                      <span className="text-[10px] text-fg-subtle">起飞质量</span>
                      <span className="text-xs font-semibold text-fg">
                        {mass.value} {mass.unit}
                      </span>
                    </div>
                    <div className="flex flex-col gap-0.5">
                      <span className="text-[10px] text-fg-subtle">有效推重比</span>
                      <span className="text-xs font-semibold text-fg">
                        {formatRatio(assessment.twr.value?.effective ?? null)}
                      </span>
                    </div>
                    <div className="flex flex-col gap-0.5">
                      <span className="text-[10px] text-fg-subtle">预估金额</span>
                      <span className="text-xs font-semibold text-fg">
                        {formatCurrency(assessment.cost.value?.total ?? null)}
                      </span>
                    </div>
                  </div>

                  <div className="flex items-center justify-between gap-2 text-[11px] text-fg-subtle">
                    <span>数据完整度 {formatPercent(assessment.completeness * 100, 0)}</span>
                    {assessment.missingAll.length > 0 ? (
                      <span className="text-caution">缺 {assessment.missingAll.length} 项参数</span>
                    ) : (
                      <span className="text-ok">参数齐全</span>
                    )}
                  </div>

                  <div className="mt-auto flex flex-wrap gap-1.5">
                    <Button size="md" variant="primary" onClick={() => navigate(`/p/${project.id}`)}>
                      打开
                    </Button>
                    <Button size="md" variant="default" onClick={() => navigate(`/p/${project.id}/wizard`)}>
                      引导填写
                    </Button>
                    <Button
                      size="md"
                      variant="ghost"
                      onClick={() => {
                        const copy = duplicate(project.id);
                        if (copy) toast.ok('已复制项目', copy.name);
                      }}
                    >
                      复制
                    </Button>
                    <Button size="md" variant="ghost" onClick={() => handleExport(project)}>
                      导出
                    </Button>
                    <Button size="md" variant="ghost" onClick={() => setPendingDelete(project)}>
                      删除
                    </Button>
                  </div>
                </CardBody>
              </Card>
            );
          })}
        </div>
      )}

      <Modal
        open={creating}
        onClose={() => setCreating(false)}
        title="新建项目"
        description="先给方案起个名字，下一步会一步步引导填写参数。"
        footer={
          <>
            <Button size="lg" variant="ghost" onClick={() => setCreating(false)}>
              取消
            </Button>
            <Button size="lg" variant="primary" onClick={handleCreate}>
              创建并开始填写
            </Button>
          </>
        }
      >
        <TextInput
          label="项目名称"
          placeholder="例如：10 英寸巡检四轴"
          value={newName}
          onChange={(event) => setNewName(event.target.value)}
          hint="留空会使用「未命名项目」"
        />
      </Modal>

      <Modal
        open={pendingDelete !== null}
        onClose={() => setPendingDelete(null)}
        title="删除项目？"
        description={pendingDelete ? `「${pendingDelete.name}」将被永久删除，无法恢复。` : undefined}
        footer={
          <>
            <Button size="lg" variant="ghost" onClick={() => setPendingDelete(null)}>
              取消
            </Button>
            <Button
              size="lg"
              variant="danger"
              onClick={() => {
                if (pendingDelete) {
                  removeProject(pendingDelete.id);
                  toast.ok('已删除项目', pendingDelete.name);
                }
                setPendingDelete(null);
              }}
            >
              删除
            </Button>
          </>
        }
      />
    </div>
  );
}
